import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import ConfirmDialog, { confirmDialog } from '../../../components/ui/ConfirmDialog'
import AttachmentTable, { type AttachmentTableRow } from '../../../components/ui/AttachmentTable'
import AttachmentPreviewPanel from '../../../components/ui/AttachmentPreviewPanel'
import FileUpload from '../../../components/ui/FileUpload'
import { useToast } from '../../../components/ui/ToastProvider'
import { GRANTS } from '../../../auth/grants'
import { useAttachmentPreview } from '../../../lib/useAttachmentPreview'
import { DOCUMENT_ACCEPT } from '../../../lib/uploadAccept'
import { useTodoTaskAttachments } from '../hooks/useTodoLists'
import type { TodoTask } from '../types'

interface Props {
  /** Açık madde; `null` → pop-up kapalı. */
  task: TodoTask | null
  /** Maddenin listesi — mutasyon sonrası satırdaki ek göstergesini tazelemek için. */
  listId: number
  onHide: () => void
  /**
   * Kısıtlı Yetkili asistan kipi (126). `true` iken **yükleme alanı ve kaldırma sütunu** düşer;
   * görüntüleme ve indirme kalır — dosyayı okumak bir okumadır.
   */
  readOnly?: boolean
}

/**
 * **Notlarım maddesi — Dosyalar** pop-up'ı (123). Prensip Kararı Dosyalar pop-up'ının (108)
 * kardeşidir ve aynı kabuğu kullanır: madde zaten oluştuğu için dosya seçilir seçilmez sunucuya gider,
 * "kaydet" adımı yoktur. Satırda Görüntüle (PDF/görsel, pop-up içinde) · İndir · Kaldır.
 *
 * <p><b>Yükleme/kaldırma tek istisna dışında koşulsuzdur:</b> Notlarım'da "görebilen = yapabilen"
 * (30.09.2026) — listeyi gören herkes maddeyi tamamlayıp silebildiği gibi dosyasını da yönetir, ayrı
 * bir yönetim yetkisi yok. İstisna <b>Kısıtlı Yetkili asistan</b>dır (126): o kipte modül okunur ama
 * yazılamaz, ölçüt yetki değil persona kipidir ({@code readOnly}).
 *
 * <p>Kabul listesi Görev Oluştur'un doküman alanıyla aynıdır ({@link DOCUMENT_ACCEPT}): ana sayfadaki
 * "Not Ekle" pop-up'ı da onu kullanır ve iki giriş yolunun farklı dosya türü kabul etmesi şaşırtırdı.
 */
export default function TodoTaskFilesDialog({
  task,
  listId,
  onHide,
  readOnly = false,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const taskId = task?.id ?? null
  const { list, upload, remove } = useTodoTaskAttachments(taskId, listId)

  // Önizleme durumu, object URL ömrü ve geç gelen blob koruması ortak hook'ta (108'deki kalıp).
  const onPreviewError = useCallback(() => toast.error(t('todo.files.previewError')), [toast, t])
  const {
    preview,
    loading: previewLoading,
    open: openPreview,
    close: closePreview,
  } = useAttachmentPreview({ scopeId: taskId, onError: onPreviewError })

  const handleAdd = (files: File[]) => {
    if (files.length === 0) return
    upload.mutate(files, {
      onSuccess: () => toast.success(t('todo.files.uploaded')),
      onError: () => toast.error(t('todo.files.uploadError')),
    })
  }

  const confirmRemove = (row: AttachmentTableRow) =>
    confirmDialog({
      group: 'todo-file-remove',
      header: t('common.removeFileConfirm.title'),
      message: t('common.removeFileConfirm.message', { name: row.fileName }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      accept: () =>
        remove.mutate(Number(row.key), {
          onSuccess: () => {
            // Kaldırılan dosya o an önizlemedeyse panel de kapanır.
            closePreview()
            toast.success(t('todo.files.removed'))
          },
          onError: () => toast.error(t('todo.files.removeError')),
        }),
    })

  const rows: AttachmentTableRow[] = (list.data ?? []).map((d) => ({
    key: String(d.id),
    fileName: d.fileName,
    extension: d.extension,
    size: d.size,
    downloadId: d.id,
    uploadedByName: d.uploadedByName,
    uploadedAt: d.uploadedAt,
  }))

  const busy = upload.isPending || remove.isPending || previewLoading

  return (
    <FormDialog
      visible={task != null}
      header={t('todo.files.title')}
      /* Footer YOK: dosya seçilir seçilmez yüklenir — kapanış ×/Esc ile. */
      hideFooter
      submitLabel=""
      onSubmit={onHide}
      onHide={onHide}
      width="64rem"
    >
      {task != null && (
        <p className="ui-dialog-subject" title={task.name}>
          {task.name}
        </p>
      )}

      {preview == null ? (
        <>
          {!readOnly && (
            <div className="todo-files-upload">
              <FileUpload
                files={[]}
                onChange={handleAdd}
                accept={DOCUMENT_ACCEPT}
                label={t('todo.files.upload')}
                hint={t('actions.detail.documents.hint')}
                // Seçilen dosya anında sunucuya gider; "bekleyen dosya" listesi yanıltıcı olurdu.
                showList={false}
              />
            </div>
          )}
          <AttachmentTable
            rows={rows}
            emptyText={t('todo.files.empty')}
            /* `undefined` geçmek kaldırma sütununu komple düşürür (AttachmentTable'ın kendi
               sözleşmesi) — görüntüleme ve indirme yerinde kalır. */
            onRemove={readOnly ? undefined : confirmRemove}
            onPreview={openPreview}
            busy={busy}
            requiredGrant={GRANTS.TODO_LIST_VIEW}
          />
        </>
      ) : (
        <AttachmentPreviewPanel preview={preview} onBack={closePreview} />
      )}

      <ConfirmDialog group="todo-file-remove" />
    </FormDialog>
  )
}
